import React, { useEffect, useState } from 'react';
import { tryParseJson } from './utils';
import './styles.css';

/**
 * CarbonWidget — beautiful embodied carbon analysis
 *
 * Expects data.response shaped as:
 *   { "<stageName>": { "<layerName>": { embodied_carbon_kgCO2e, carbon_intensity_kgCO2e_per_m3, annotation } } }
 */

// ── Palette & helpers ──────────────────────────────────────────────────────

const SYS_COLORS = [
    '#2e7d32','#1565c0','#6a1b9a','#e65100','#00838f',
    '#f57f17','#4527a0','#ad1457','#37474f','#558b2f'
];

const fmt = (v, d = 2) =>
    Number(v).toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d });

// Parse "Carbon_Layer_Structural_Wall_firstfloor" → { element:"Wall", floor:"First Floor" }
const parseLayerName = (layerName, systemName) => {
    // Strip "Carbon_Layer_" prefix and system name
    let rest = layerName.replace(/^Carbon_Layer_/i, '');
    // Remove system name from start (case-insensitive)
    const sysPattern = new RegExp(`^${systemName}_`, 'i');
    rest = rest.replace(sysPattern, '');
    // Last segment is the floor
    const parts = rest.split('_');
    const floorRaw = parts[parts.length - 1];
    const floorNice = floorRaw.replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/floor/i, ' Floor')
        .replace(/\b\w/g, c => c.toUpperCase())
        .trim();
    const elementParts = parts.slice(0, -1);
    const element = elementParts.join(' ') || layerName;
    return { element, floor: floorNice };
};

const systemIcon = (name = '') => {
    const n = name.toLowerCase();
    if (n.includes('structural') || n.includes('structure')) return '🏗️';
    if (n.includes('hvac') || n.includes('heat') || n.includes('cool') || n.includes('vent')) return '🌡️';
    if (n.includes('electr')) return '⚡';
    if (n.includes('plumb') || n.includes('water') || n.includes('pipe')) return '🚰';
    if (n.includes('fire') || n.includes('sprinkler')) return '🚒';
    if (n.includes('light') || n.includes('illum')) return '💡';
    return '🏢';
};

// Carbon intensity colour: green low, amber mid, red high
const intensityColor = (v) => v > 200 ? '#c62828' : v > 100 ? '#ef6c00' : '#2e7d32';

// Leaf rating A++…G from total carbon
const carbonRating = (total) => {
    if (total < 100)  return { grade: 'A++', color: '#1b5e20' };
    if (total < 300)  return { grade: 'A+',  color: '#2e7d32' };
    if (total < 600)  return { grade: 'A',   color: '#388e3c' };
    if (total < 1000) return { grade: 'B',   color: '#f9a825' };
    if (total < 2000) return { grade: 'C',   color: '#e65100' };
    return               { grade: 'D',   color: '#c62828' };
};

// ── Sub-components ─────────────────────────────────────────────────────────

const HorizBar = ({ pct, color }) => (
    <div style={{ flex: 1, height: 10, background: '#e8f5e9', borderRadius: 5, overflow: 'hidden', minWidth: 60 }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 5,
            transition: 'width .5s ease' }} />
    </div>
);

// ── Main component ─────────────────────────────────────────────────────────

const CarbonWidget = ({ data, onComplete }) => {
    const [systems, setSystems]           = useState([]);   // [{ name, color, icon, totalCarbon, elements }]
    const [totalCarbon, setTotalCarbon]   = useState(0);
    const [expandedEl, setExpandedEl]     = useState(null); // layer key of expanded element
    const [activeTab, setActiveTab]       = useState('ranking'); // 'ranking' | 'systems'
    const [isLoading, setIsLoading]       = useState(true);

    useEffect(() => {
        if (data?.response) {
            try {
                const parsed = tryParseJson(data.response);
                if (parsed && typeof parsed === 'object') buildData(parsed);
            } catch (e) { console.error('[CarbonWidget]', e); }
            setIsLoading(false);
            if (onComplete) onComplete('Carbon widget rendered');
        }
    }, [data]);

    const buildData = (parsed) => {
        let grand = 0;
        const sysArr = Object.entries(parsed).map(([sysName, layers], si) => {
            const elements = Object.entries(layers || {}).map(([layerKey, attrs]) => {
                const { element, floor } = parseLayerName(layerKey, sysName);
                const carbon    = parseFloat(attrs['embodied_carbon_kgCO2e']) || 0;
                const intensity = parseFloat(attrs['carbon_intensity_kgCO2e_per_m3']) || 0;
                const annotation= attrs['annotation'] || '';
                grand += carbon;
                return { layerKey, element, floor, carbon, intensity, annotation };
            });
            const totalC = elements.reduce((s, e) => s + e.carbon, 0);
            return {
                name: sysName,
                color: SYS_COLORS[si % SYS_COLORS.length],
                icon: systemIcon(sysName),
                totalCarbon: totalC,
                elements
            };
        });
        setTotalCarbon(grand);
        setSystems(sysArr);
    };

    if (isLoading) return <div className="archie-widget-loading">Loading carbon data…</div>;
    if (!systems.length) return <div className="kpi-empty-state"><p>No carbon data available</p></div>;

    // Flat list sorted by carbon descending (for ranking tab)
    const allElements = systems.flatMap(s =>
        s.elements.map(e => ({ ...e, sysName: s.name, sysColor: s.color }))
    ).sort((a, b) => b.carbon - a.carbon);

    const maxCarbon = allElements[0]?.carbon || 1;
    const rating    = carbonRating(totalCarbon);

    // Tab styles
    const tab = (id) => ({
        padding: '8px 18px', border: 'none', cursor: 'pointer', borderRadius: '6px 6px 0 0',
        fontSize: 13, fontWeight: 700,
        background: activeTab === id ? '#fff' : 'transparent',
        color:      activeTab === id ? '#1b5e20' : '#a5d6a7',
        borderBottom: activeTab === id ? '3px solid #2e7d32' : '3px solid transparent',
        transition: 'all .2s'
    });

    return (
        <div style={{ fontFamily: '"Suisse Intl", sans-serif', padding: '16px 4px', color: '#1b2a1b' }}>

            {/* ── Header card ──────────────────────────────────────────── */}
            <div style={{
                background: 'linear-gradient(135deg,#1b5e20 0%,#2e7d32 60%,#388e3c 100%)',
                borderRadius: 14, padding: '20px 24px', marginBottom: 20,
                boxShadow: '0 6px 24px rgba(27,94,32,.4)',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16
            }}>
                <div>
                    <div style={{ color: '#a5d6a7', fontSize: 12, fontWeight: 600,
                        textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 4 }}>
                        🌱 Embodied Carbon Analysis
                    </div>
                    <div style={{ color: '#fff', fontSize: 30, fontWeight: 800, lineHeight: 1.1 }}>
                        {fmt(totalCarbon)} <span style={{ fontSize: 16, fontWeight: 500 }}>kg CO₂e</span>
                    </div>
                    <div style={{ color: '#a5d6a7', fontSize: 12, marginTop: 6 }}>
                        {systems.length} systems · {allElements.length} elements
                    </div>
                </div>

                {/* Rating badge */}
                <div style={{
                    background: 'rgba(255,255,255,.15)', borderRadius: 12,
                    padding: '14px 20px', textAlign: 'center', backdropFilter: 'blur(4px)'
                }}>
                    <div style={{ fontSize: 32, fontWeight: 900, color: '#fff',
                        textShadow: '0 2px 8px rgba(0,0,0,.3)' }}>{rating.grade}</div>
                    <div style={{ fontSize: 10, color: '#c8e6c9', fontWeight: 600,
                        textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: 2 }}>
                        Carbon Rating
                    </div>
                </div>
            </div>

            {/* ── System summary strips ─────────────────────────────────── */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
                {systems.map((s, i) => (
                    <div key={i} style={{
                        flex: '1 1 140px', background: '#fff', borderRadius: 10,
                        border: `2px solid ${s.color}30`, borderLeft: `5px solid ${s.color}`,
                        padding: '10px 14px', boxShadow: '0 2px 8px rgba(0,0,0,.06)'
                    }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#263238', marginBottom: 3 }}>
                            {s.icon} {s.name}
                        </div>
                        <div style={{ fontSize: 18, fontWeight: 800, color: s.color }}>
                            {fmt(s.totalCarbon, 1)}
                        </div>
                        <div style={{ fontSize: 11, color: '#90a4ae' }}>
                            kg CO₂e &nbsp;·&nbsp; {((s.totalCarbon / totalCarbon) * 100).toFixed(1)}%
                        </div>
                        <div style={{ height: 5, background: '#e8f5e9', borderRadius: 3, marginTop: 8, overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${(s.totalCarbon / totalCarbon) * 100}%`,
                                background: s.color, borderRadius: 3 }} />
                        </div>
                    </div>
                ))}
            </div>

            {/* ── Tabs ─────────────────────────────────────────────────── */}
            <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid #e8f5e9', marginBottom: 16 }}>
                <button onClick={() => setActiveTab('ranking')} style={tab('ranking')}>🏆 Top Emitters</button>
                <button onClick={() => setActiveTab('systems')} style={tab('systems')}>🏗️ By System</button>
            </div>

            {/* ── Ranking tab ───────────────────────────────────────────── */}
            {activeTab === 'ranking' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {allElements.map((el, i) => {
                        const pct = (el.carbon / maxCarbon) * 100;
                        const key = el.layerKey;
                        const isOpen = expandedEl === key;
                        return (
                            <div key={key} style={{
                                background: '#fff', borderRadius: 10, overflow: 'hidden',
                                border: `1px solid ${isOpen ? el.sysColor : '#e8f0e8'}`,
                                boxShadow: isOpen ? `0 4px 16px ${el.sysColor}30` : '0 2px 6px rgba(0,0,0,.05)',
                                transition: 'all .2s'
                            }}>
                                {/* Row */}
                                <div
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => setExpandedEl(isOpen ? null : key)}
                                    onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && setExpandedEl(isOpen ? null : key)}
                                    style={{ display: 'flex', alignItems: 'center', gap: 8,
                                        padding: '10px 14px', cursor: 'pointer' }}
                                >
                                    {/* Rank badge */}
                                    <div style={{
                                        width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
                                        background: i < 3 ? el.sysColor : '#e8f0e8',
                                        color: i < 3 ? '#fff' : '#546e7a',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        fontSize: 10, fontWeight: 700
                                    }}>{i + 1}</div>

                                    {/* Name + bar — takes all remaining space */}
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontWeight: 700, fontSize: 12, color: '#263238',
                                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {el.element}
                                        </div>
                                        <div style={{ fontSize: 10, color: '#90a4ae', marginBottom: 4 }}>{el.floor}</div>
                                        <HorizBar pct={pct} color={el.sysColor} />
                                    </div>

                                    {/* Value — fixed right column, never hidden */}
                                    <div style={{ flexShrink: 0, textAlign: 'right', minWidth: 72 }}>
                                        <div style={{ fontWeight: 800, fontSize: 13, color: el.sysColor, whiteSpace: 'nowrap' }}>
                                            {fmt(el.carbon, 1)}
                                        </div>
                                        <div style={{ fontSize: 10, color: '#90a4ae' }}>kg CO₂e</div>
                                    </div>

                                    {/* Toggle chevron */}
                                    <div style={{ fontSize: 12, color: '#90a4ae', flexShrink: 0 }}>
                                        {isOpen ? '▲' : '▼'}
                                    </div>
                                </div>

                                {/* Expanded details */}
                                {isOpen && (
                                    <div style={{
                                        borderTop: `1px solid ${el.sysColor}30`,
                                        background: `${el.sysColor}08`, padding: '14px 16px'
                                    }}>
                                        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
                                            {[
                                                { label: 'Embodied Carbon', value: `${fmt(el.carbon)} kg CO₂e` },
                                                { label: 'Carbon Intensity', value: `${fmt(el.intensity, 2)} kg CO₂e/m³`,
                                                  valueColor: intensityColor(el.intensity) },
                                                { label: '% of Total', value: `${((el.carbon / totalCarbon) * 100).toFixed(2)}%` },
                                                { label: 'System', value: el.sysName }
                                            ].map((m, mi) => (
                                                <div key={mi} style={{
                                                    background: '#fff', borderRadius: 8, padding: '8px 14px',
                                                    border: `1px solid ${el.sysColor}30`,
                                                    boxShadow: '0 1px 4px rgba(0,0,0,.05)'
                                                }}>
                                                    <div style={{ fontSize: 10, color: '#90a4ae', textTransform: 'uppercase',
                                                        letterSpacing: '0.5px', fontWeight: 600 }}>{m.label}</div>
                                                    <div style={{ fontSize: 14, fontWeight: 700,
                                                        color: m.valueColor || '#1b5e20', marginTop: 2 }}>{m.value}</div>
                                                </div>
                                            ))}
                                        </div>
                                        {el.annotation && (
                                            <div style={{
                                                background: '#e8f5e9', border: `1px solid ${el.sysColor}40`,
                                                borderRadius: 8, padding: '10px 14px',
                                                fontSize: 12, color: '#1b5e20', lineHeight: 1.6
                                            }}>
                                                <span style={{ fontWeight: 700 }}>📝 </span>{el.annotation}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* ── By-system tab ─────────────────────────────────────────── */}
            {activeTab === 'systems' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {systems.map((s, si) => {
                        const sysMax = Math.max(...s.elements.map(e => e.carbon), 1);
                        return (
                            <div key={si} style={{
                                background: '#fff', borderRadius: 12, overflow: 'hidden',
                                border: `1px solid ${s.color}30`,
                                boxShadow: '0 2px 10px rgba(0,0,0,.07)'
                            }}>
                                {/* System header */}
                                <div style={{
                                    background: `linear-gradient(90deg,${s.color} 0%,${s.color}cc 100%)`,
                                    padding: '12px 18px', display: 'flex',
                                    justifyContent: 'space-between', alignItems: 'center'
                                }}>
                                    <span style={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>
                                        {s.icon} {s.name}
                                    </span>
                                    <span style={{ color: '#fff', fontWeight: 800, fontSize: 15 }}>
                                        {fmt(s.totalCarbon, 1)} kg CO₂e
                                    </span>
                                </div>
                                {/* Elements */}
                                <div style={{ padding: '12px 18px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                                    {s.elements.sort((a, b) => b.carbon - a.carbon).map((el, ei) => (
                                        <div key={ei}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between',
                                                alignItems: 'center', marginBottom: 4 }}>
                                                <div>
                                                    <span style={{ fontWeight: 600, fontSize: 13, color: '#263238' }}>
                                                        {el.element}
                                                    </span>
                                                    <span style={{ fontSize: 11, color: '#90a4ae', marginLeft: 6 }}>
                                                        {el.floor}
                                                    </span>
                                                </div>
                                                <div style={{ textAlign: 'right' }}>
                                                    <span style={{ fontWeight: 700, fontSize: 13, color: s.color }}>
                                                        {fmt(el.carbon, 1)} kg CO₂e
                                                    </span>
                                                    <span style={{ fontSize: 11, color: '#90a4ae', marginLeft: 8,
                                                        padding: '2px 6px', background: `${intensityColor(el.intensity)}15`,
                                                        borderRadius: 4, color: intensityColor(el.intensity), fontWeight: 600 }}>
                                                        {fmt(el.intensity, 1)} kg/m³
                                                    </span>
                                                </div>
                                            </div>
                                            <div style={{ height: 6, background: '#e8f5e9', borderRadius: 3, overflow: 'hidden' }}>
                                                <div style={{ height: '100%', borderRadius: 3, background: s.color,
                                                    width: `${(el.carbon / sysMax) * 100}%` }} />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default CarbonWidget;